import i18next, { t } from "i18next";
import { CircleCheck } from "lucide-react";
import { toast } from "sonner";

/** Durée d'affichage du toast de mise à jour applicative (30 s). */
const APP_UPDATE_TOAST_DURATION = 30 * 1000;

/** Forme minimale d'une erreur API (ky, fetch ou équivalent). */
export interface ErrorAppInterface {
  data?: Record<string, unknown>;
  response?: {
    status?: number;
    statusCode?: number;
    data?: Record<string, unknown>;
  };
  message?: string;
}

export interface NotifSuccessResponse {
  requiresMfa: boolean;
  message: string;
  operationId: string;
}

const notifClar = (message: string, duration?: number) => {
  toast.info(t(message), {
    duration: duration ? duration : 10000,
    action: {
      label: t("hide"),
      onClick: () => console.log(t("hide")),
    },
  });
};

const notifClarSound = (message: string, duration?: number) => {
  // Son optionnel : déposer le fichier dans `public/audio/notification-sound.wav`.
  // En l'absence du fichier, la lecture échoue silencieusement (warn console).
  const audio = new Audio("/audio/notification-sound.wav");
  audio.volume = 1;

  audio.play().catch((err) => {
    console.warn("Le son n'a pas pu être joué :", err);
  });
  toast.info(t(message), {
    duration: duration ? duration : 10000,
    action: {
      label: t("hide"),
      onClick: () => console.log(t("hide")),
    },
  });
};

/**
 * Toast de succès (sonner) aux couleurs de la charte : surface douce
 * `success-muted`, bordure et texte `success` (même langage que les badges
 * `border-success/40 bg-success-muted text-success`), icône lucide et bouton
 * de fermeture natif du Toaster. Les utilitaires `!` priment sur le style
 * `richColors` du Toaster ; les tokens s'adaptent au mode sombre.
 */
const notifSilence = (message: string, duration?: number) => {
  toast.success(t(message), {
    duration: duration ? duration : 10000,
    icon: <CircleCheck className="size-5 text-success" />,
    classNames: {
      toast: "!border-success/40 !bg-success-muted",
      title: "!text-success",
      closeButton: "!border-success/40 !bg-success-muted !text-success",
    },
  });
};

const notifSilenceError = (message: string, duration?: number) => {
  toast.error(t(message), {
    duration: duration ? duration : 10000,
    action: {
      label: t("hide"),
      onClick: () => console.log(t("hide")),
    },
  });
};

const notifSilenceWarning = (message: string, duration?: number) => {
  toast.warning(t(message), {
    duration: duration ? duration : 10000,
    action: {
      label: t("hide"),
      onClick: () => console.log(t("hide")),
    },
  });
};

/**
 * Identifiant stable partagé par toutes les sources de détection de mise à jour
 * (polling `version.json` et service worker), afin que sonner dédoublonne le
 * toast au lieu d'en empiler un par source.
 */
const APP_UPDATE_TOAST_ID = "app-update-available";

interface NotifUpdateAvailableParams {
  onReload: () => void;
  actionLabelKey?: string;
}

const notifUpdateAvailable = ({
  onReload,
  actionLabelKey = "reload",
}: NotifUpdateAvailableParams) => {
  toast.info(t("newVersionAvailable"), {
    id: APP_UPDATE_TOAST_ID,
    description: t("newVersionMessage"),
    duration: APP_UPDATE_TOAST_DURATION,
    action: {
      label: t(actionLabelKey),
      onClick: onReload,
    },
  });
};

// Équivalent sonner le plus proche d'une modale bloquante (ex-SweetAlert) :
// pas d'auto-dismiss, l'utilisateur doit fermer explicitement le toast.
const notifLevelSecure = () => {
  toast.warning(t("actionRestrict"), {
    description: t("restrictActionMessage"),
    duration: Infinity,
  });
};

const notifInfoApproval = () => {
  toast.info(t("actionApproval"), {
    description: t("approvalActionMessage"),
    duration: Infinity,
  });
};

/**
 * Lit le corps d'erreur côté backend en couvrant axios (`error.response.data`)
 * et ky (`error.data`, car `error.response` est une `Response` native).
 */
const getErrorBody = (error: unknown) => {
  if (!error || typeof error !== "object") return undefined;
  const err = error as {
    data?: Record<string, unknown>;
    response?: { data?: Record<string, unknown> };
  };
  return err.data ?? err.response?.data;
};

const getErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== "object") return undefined;
  const err = error as {
    response?: { status?: number; statusCode?: number };
  };
  return err.response?.status ?? err.response?.statusCode;
};

const getErrorMessageByLanguage = (
  error: ErrorAppInterface,
  message: string
) => {
  const errorMessage = getErrorBody(error)?.message;

  // Si le message est un objet multilingue
  if (typeof errorMessage === "object" && errorMessage !== null) {
    const msgObj = errorMessage as Record<string, string | undefined>;
    switch (i18next.language) {
      case "fr":
        return msgObj.fr ?? message;
      case "en":
        return msgObj.en ?? message;
      case "zh":
      case "cn":
        return msgObj.cn ?? msgObj.zh ?? message;
      default:
        return message;
    }
  }

  // Si le message est une string directe du backend
  if (typeof errorMessage === "string" && errorMessage) {
    return errorMessage;
  }

  return message;
};

const notifBusinessError = (error: ErrorAppInterface) => {
  const errorMessage = getErrorMessageByLanguage(error, t("error"));
  const statusCode = getErrorStatus(error);
  const serverCode = getErrorBody(error)?.code;

  toast.error(t("alertJob"), {
    description: statusCode
      ? `${errorMessage}\n${t("errorCode")}: ${statusCode}${serverCode ? ` | ${t("serverCode")}: ${serverCode}` : ""}`
      : errorMessage,
    duration: Infinity,
  });
};

const notifErrorGlobal = (error: ErrorAppInterface, message: string) => {
  const status = getErrorStatus(error);
  const code = getErrorBody(error)?.code;

  if (status === 403 && code === 1000) {
    notifSilenceWarning(getErrorMessageByLanguage(error, message), 7000);
  }

  if (status === 403 && code !== 1000) {
    notifSilenceWarning(message, 7000);
  } else if (status !== 403) {
    notifSilenceError(message, 10000);
  }
};

const notifSuccessGlobal = (
  status: number,
  message: string,
  operationId?: string
): NotifSuccessResponse => {
  if (status === 202) {
    return {
      requiresMfa: true,
      message,
      operationId: operationId || "",
    };
  } else {
    notifSilence(message, 7000);
    return {
      requiresMfa: false,
      message,
      operationId: operationId || "",
    };
  }
};

export {
  notifBusinessError,
  notifClar,
  notifClarSound,
  notifErrorGlobal,
  notifInfoApproval,
  notifLevelSecure,
  notifSilence,
  notifSilenceError,
  notifSilenceWarning,
  notifSuccessGlobal,
  notifUpdateAvailable,
};
